import React from 'react';
import { Archive, Check, CheckCheck, ChevronLeft, ChevronRight, File, Folder, Mail, Menu, RefreshCw, Search, Star, Trash2, X } from 'lucide-react';
import { buildInboxAttachmentDownloadUrl, type InboxAttachmentPreviewTarget } from '../inboxAttachmentUtils';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { projectDetailPath } from '../../../utils/projectRoutes';
import {
  TRIAGE_SECTIONS,
  getTriageBucket,
  getCounterpartyAddr,
  getEffectiveMailStatus,
  getClassificationLabel,
  isUrgent,
  MAIL_STATUS_DOT,
  displayNameFromAddr,
  type InboxEmail,
  type LeadOption,
  type SortMode,
  INBOX_EMAIL_DND_TYPE,
  Avatar,
} from '../inboxShared';

function looksLikeLeadUuid(value: string): boolean {
  return /^#?[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

interface InboxThreadListProps {
  threads: InboxEmail[];
  accountEmail: string;
  selectedAccountId: string | null;
  accountOptions: Array<{ value: string; label: string }>;
  syncButtonLabel: string;
  syncButtonTitle: string;
  syncIconSpinning: boolean;
  emailsFetching: boolean;
  onAccountChange: (value: string | null) => void;
  onSync: () => void;
  selectedMailId: string | number | null;
  /** Highlights the thread row when the URL id is not the list representative id. */
  selectedThreadKey?: string | null;
  onSelect: (id: string | number) => void;
  searchTerm: string;
  onSearch: (v: string) => void;
  statusFilter: string[];
  onStatusFilterChange: (ids: string[]) => void;
  sortMode: SortMode;
  onSortMode: (m: SortMode) => void;
  getThreadCount: (mail: InboxEmail) => number;
  leadsForLink: LeadOption[];
  onOpenProject: (leadId: string | number) => void;
  initialLoading: boolean;
  hasAnyEmails: boolean;
  onNewMail: () => void;
  isMobile: boolean;
  hasMore: boolean;
  pageIndex: number;
  pageSize: number;
  loadingMore: boolean;
  onNextPage: () => void;
  onPrevPage: () => void;
  onToggleStar: (mail: InboxEmail) => void;
  onOpenAttachment: (attachment: InboxAttachmentPreviewTarget) => void;
  onArchive: (mail: InboxEmail) => void;
  onDelete: (mail: InboxEmail) => void;
  onMarkRead: (mail: InboxEmail) => void;
  onMarkDone: (mail: InboxEmail) => void;
  onBulkAction: (mails: InboxEmail[], action: 'archive' | 'delete' | 'done') => Promise<void> | void;
  onOpenFolders?: () => void;
  totalCount: number;
  /** When false, arrow/enter navigation is paused (open modals). */
  keyboardEnabled?: boolean;
  /** Rows moved into a category. They collapse, then the page drops them. */
  leavingIds?: string[];
}

export const INBOX_ROW_LEAVE_MS = 320;

/** Gmail-style exit: slide and fade, then close the gap. */
function animateRowLeave(row: HTMLElement) {
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const height = row.offsetHeight;
  row.style.overflow = 'hidden';
  row.style.pointerEvents = 'none';
  if (reduced || typeof row.animate !== 'function') {
    row.style.opacity = '0';
    return;
  }
  row.animate(
    [
      { opacity: 1, transform: 'translateX(0)', height: `${height}px` },
      { opacity: 0, transform: 'translateX(24px)', height: `${height}px`, offset: 0.5 },
      { opacity: 0, transform: 'translateX(24px)', height: '0px', borderBottomWidth: '0px' },
    ],
    { duration: INBOX_ROW_LEAVE_MS, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' },
  );
}

function RowHoverAction({
  title,
  onClick,
  icon: Icon,
}: {
  title: string;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="inline-flex h-7 w-7 items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover"
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
    </button>
  );
}

function isCalendarInviteFile(filename: string, contentType?: string | null): boolean {
  const name = filename.trim().toLowerCase();
  const type = String(contentType ?? '').toLowerCase();
  return name.endsWith('.ics') || type.includes('text/calendar') || type.includes('application/ics');
}

function inboxDragPreviewLabel(count: number): string {
  return count === 1 ? '1 Konversation wird verschoben' : `${count} Konversationen werden verschoben`;
}

function createInboxDragPreview(count: number): HTMLElement {
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.textContent = inboxDragPreviewLabel(count);
  Object.assign(el.style, {
    position: 'fixed',
    top: '-240px',
    left: '16px',
    zIndex: '10000',
    padding: '10px 16px',
    borderRadius: '8px',
    background: '#0b57d0',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '700',
    lineHeight: '20px',
    letterSpacing: '0.01em',
    boxShadow: '0 8px 20px rgba(11,87,208,0.35)',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  });
  document.body.appendChild(el);
  return el;
}

function mailPreviewText(snippet: string | null | undefined): string {
  return String(snippet ?? '').replace(/\s+/g, ' ').trim();
}

function visibleRowOrder(threads: InboxEmail[], sortMode: SortMode, isMobile: boolean): InboxEmail[] {
  if (sortMode !== 'prio' || isMobile) return threads;
  return TRIAGE_SECTIONS.flatMap((sec) => threads.filter((mail) => getTriageBucket(mail) === sec.key));
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return Boolean(el.closest('[contenteditable="true"]'));
}

function fmtTime(dateAt: string | null): string {
  if (!dateAt) return '';
  const d = new Date(dateAt);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
}

export function InboxThreadList({
  threads,
  accountEmail,
  syncButtonLabel,
  syncButtonTitle,
  syncIconSpinning,
  emailsFetching,
  onSync,
  onSelect,
  searchTerm,
  onSearch,
  sortMode,
  getThreadCount,
  leadsForLink,
  onOpenProject,
  initialLoading,
  hasAnyEmails,
  isMobile,
  hasMore,
  pageIndex,
  pageSize,
  loadingMore,
  onNextPage,
  onPrevPage,
  onToggleStar,
  onOpenAttachment,
  onArchive,
  onDelete,
  onMarkRead,
  onMarkDone,
  onBulkAction,
  onOpenFolders,
  totalCount,
  keyboardEnabled = true,
  leavingIds,
}: InboxThreadListProps) {
  const { canOpen } = useModuleAccess();
  const { can } = useCapabilities();
  const canViewProjects = can('web.projekte.view');
  const projectRef = React.useCallback(
    (mail: InboxEmail): { id: string | number; label: string; title: string } | null => {
      if (!canViewProjects) return null;
      const leadId = mail.lead_id;
      if (leadId == null || leadId === '') return null;
      const lead = leadsForLink.find((row) => String(row.id) === String(leadId));
      const label = String(mail.lead_display_id || lead?.display_id || '').trim();
      if (!label || looksLikeLeadUuid(label)) return null;
      const title = String(mail.lead_title || lead?.lead_title || lead?.name || label).trim();
      return { id: leadId, label, title };
    },
    [leadsForLink, canViewProjects],
  );

  const renderChips = (mail: InboxEmail, chipMaxWidth: string) => {
    const project = projectRef(mail);
    const projectOpenable = project != null && canOpen(projectDetailPath({ id: project.id }));
    const listAttachments = (mail.attachments ?? [])
      .filter((attachment) => attachment.filename && attachment.id && !isCalendarInviteFile(attachment.filename, attachment.content_type))
      .slice(0, 2);
    if (!project && listAttachments.length === 0) return null;
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {project ? (
          <button
            type="button"
            title={projectOpenable ? project.title : 'Nicht freigeschaltet'}
            disabled={!projectOpenable}
            onClick={(event) => {
              event.stopPropagation();
              onOpenProject(project.id);
            }}
            className={`inline-flex h-[26px] ${chipMaxWidth} shrink-0 items-center gap-1.5 rounded-full border border-app-border bg-app-surface px-2.5 text-xs text-app-text-muted hover:bg-app-hover disabled:cursor-default disabled:hover:bg-app-surface`}
          >
            <Folder className="h-3.5 w-3.5 shrink-0 text-pipedrive-blue" strokeWidth={2} />
            <span className="truncate">{project.label}</span>
          </button>
        ) : null}
        {listAttachments.map((attachment) => {
          const filename = attachment.filename;
          const messageId = mail.external_message_id;
          const accountId = mail.account_id;
          if (!messageId || accountId == null || !attachment.id) return null;
          const target: InboxAttachmentPreviewTarget = {
            downloadUrl: buildInboxAttachmentDownloadUrl({
              accountId,
              messageId,
              attachmentId: attachment.id,
              filename,
            }),
            filename,
            contentType: attachment.content_type ?? null,
          };
          return (
            <button
              key={attachment.id}
              type="button"
              title={filename}
              onClick={(event) => {
                event.stopPropagation();
                onOpenAttachment(target);
              }}
              className={`inline-flex h-[26px] ${chipMaxWidth} shrink-0 items-center gap-1.5 rounded-full border border-app-border bg-app-surface px-2.5 text-xs text-app-text-muted hover:bg-app-hover`}
            >
              <File className="h-3.5 w-3.5 shrink-0 text-red-500" strokeWidth={2} />
              <span className="truncate">{filename}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((current) => (current.includes(id) ? current.filter((row) => row !== id) : [...current, id]));
  };

  /** Gmail mobile: avatar, sender + time, subject, snippet + star. Tapping the avatar selects. */
  const renderMobileRow = (mail: InboxEmail) => {
    const id = String(mail.id);
    const counterpartyAddr = getCounterpartyAddr(mail, accountEmail);
    const unread = mail.is_unread === true;
    const count = mail.thread_count ?? getThreadCount(mail);
    const preview = mailPreviewText(mail.snippet);
    const status = getEffectiveMailStatus(mail);
    const statusLabel = getClassificationLabel(status);
    const selected = selectedIds.includes(id);
    const selecting = selectedIds.length > 0;
    const strong = unread ? 'font-semibold text-app-text' : 'font-normal text-app-text-muted';
    const chips = renderChips(mail, 'max-w-[160px]');
    return (
      <li key={mail.id} data-inbox-row-item={id}>
        <div
          role="option"
          aria-selected={selected}
          data-inbox-row={id}
          onClick={() => (selecting ? toggleSelected(id) : onSelect(mail.id))}
          className={`flex cursor-pointer items-start gap-3 px-4 py-3 active:bg-app-hover ${selected ? 'bg-inbox-selected' : ''}`}
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toggleSelected(id);
            }}
            aria-label={selected ? 'Auswahl aufheben' : 'E-Mail auswählen'}
            aria-pressed={selected}
            className="mt-0.5 shrink-0 rounded-full"
          >
            {selected ? (
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-pipedrive-blue text-white">
                <Check className="h-5 w-5" strokeWidth={2.5} />
              </span>
            ) : (
              <Avatar nameOrAddr={counterpartyAddr} size={40} />
            )}
          </button>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex min-w-0 items-baseline gap-2">
              <span className={`min-w-0 truncate text-[15px] ${strong}`}>{displayNameFromAddr(counterpartyAddr)}</span>
              {count > 1 ? <span className="shrink-0 text-xs text-app-text-subtle">{count}</span> : null}
              {isUrgent(mail) ? (
                <span className="shrink-0 font-semibold text-rose-600" title="Dringend">!</span>
              ) : null}
              <span className={`ml-auto shrink-0 text-xs tabular-nums ${unread ? 'font-semibold text-app-text' : 'text-app-text-subtle'}`}>
                {fmtTime(mail.date_at)}
              </span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className={`min-w-0 flex-1 truncate text-sm ${strong}`}>{mail.subject || '(Kein Betreff)'}</span>
              <span
                role="img"
                title={statusLabel}
                aria-label={statusLabel}
                className={`h-2 w-2 shrink-0 rounded-full ${MAIL_STATUS_DOT[status]}`}
              />
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-sm text-app-text-subtle">{preview || ' '}</span>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleStar(mail);
                }}
                className={`-m-1.5 shrink-0 p-1.5 ${mail.starred ? 'text-amber-500' : 'text-app-text-subtle'}`}
                aria-label={mail.starred ? 'Markierung entfernen' : 'Markieren'}
              >
                <Star className={`h-5 w-5 ${mail.starred ? 'fill-current' : ''}`} strokeWidth={mail.starred ? 1.8 : 1.5} />
              </button>
            </div>
            {chips ? <div className="mt-1.5">{chips}</div> : null}
          </div>
        </div>
      </li>
    );
  };

  const renderRow = (mail: InboxEmail) => {
    if (isMobile) return renderMobileRow(mail);
    const counterpartyAddr = getCounterpartyAddr(mail, accountEmail);
    const unread = mail.is_unread === true;
    const count = mail.thread_count ?? getThreadCount(mail);
    const chips = renderChips(mail, 'max-w-[200px]');
    const preview = mailPreviewText(mail.snippet);
    const status = getEffectiveMailStatus(mail);
    const statusLabel = getClassificationLabel(status);
    const textTone = unread ? 'font-semibold text-app-text' : 'font-normal text-app-text';
    const rowHovered = hoveredId === String(mail.id);
    const rowFocused = focusedId === String(mail.id);
    const selected = selectedIds.includes(String(mail.id));
    const dragging = draggingIds.includes(String(mail.id));
    return (
      <li key={mail.id} data-inbox-row-item={String(mail.id)} className="border-b border-app-border last:border-b-0">
        <div
          role="option"
          aria-selected={rowFocused}
          data-inbox-row={String(mail.id)}
          draggable={!isMobile}
          onDragStart={(event) => {
            const id = String(mail.id);
            const ids = selectedIds.includes(id) && selectedIds.length > 1 ? selectedIds : [id];
            event.dataTransfer.setData(INBOX_EMAIL_DND_TYPE, JSON.stringify(ids));
            event.dataTransfer.setData('text/plain', ids.join(','));
            event.dataTransfer.effectAllowed = 'move';
            const ghost = createInboxDragPreview(ids.length);
            dragPreviewRef.current = ghost;
            event.dataTransfer.setDragImage(ghost, 20, 22);
            setDraggingIds(ids);
          }}
          onDragEnd={() => {
            dragPreviewRef.current?.remove();
            dragPreviewRef.current = null;
            setDraggingIds([]);
          }}
          onClick={() => onSelect(mail.id)}
          onMouseEnter={() => setHoveredId(String(mail.id))}
          onMouseLeave={() => {
            setHoveredId((current) => (current === String(mail.id) ? null : current));
          }}
          className={`relative flex min-h-11 cursor-pointer items-center gap-3 px-3 py-2 ${
            dragging
              ? 'cursor-grabbing bg-inbox-selected-strong opacity-70'
              : selected || rowFocused
                ? 'bg-inbox-selected hover:bg-inbox-selected-strong'
                : unread
                  ? 'bg-app-surface hover:bg-app-hover'
                  : 'bg-app-surface-muted hover:bg-app-hover'
          }`}
        >
          <input
            type="checkbox"
            checked={selected}
            onClick={(event) => event.stopPropagation()}
            onChange={() => toggleSelected(String(mail.id))}
            className="h-4 w-4 shrink-0 accent-pipedrive-blue"
            aria-label="E-Mail auswählen"
          />
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleStar(mail);
            }}
            className={`shrink-0 rounded-md p-1 ${mail.starred ? 'text-amber-500' : 'text-app-text-subtle hover:text-app-text-muted'}`}
            aria-label={mail.starred ? 'Markierung entfernen' : 'Markieren'}
          >
            <Star className={`h-4 w-4 ${mail.starred ? 'fill-current' : ''}`} strokeWidth={mail.starred ? 1.8 : 1.5} />
          </button>
          <span className="flex w-36 shrink-0 items-baseline text-left text-sm">
            <span className={`min-w-0 truncate ${textTone}`}>{displayNameFromAddr(counterpartyAddr)}</span>
            {count > 1 ? <span className="ml-1 shrink-0 text-xs font-normal text-app-text-subtle">{count}</span> : null}
            {isUrgent(mail) ? (
              <span className="ml-1 shrink-0 font-semibold text-rose-600" title="Dringend">!</span>
            ) : null}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <span className="block min-w-0 truncate text-sm">
              <span className={textTone}>{mail.subject || '(Kein Betreff)'}</span>
              {preview ? <span className="font-normal text-app-text-subtle"> — {preview}</span> : null}
            </span>
            {chips}
          </div>
          <span className={`w-14 shrink-0 text-right text-xs tabular-nums ${unread ? 'font-semibold text-app-text' : 'font-normal text-app-text-subtle'} ${rowHovered ? 'invisible' : ''}`}>
            {fmtTime(mail.date_at)}
          </span>
          <span
            role="img"
            title={statusLabel}
            aria-label={statusLabel}
            className={`h-2.5 w-2.5 shrink-0 rounded-sm ${MAIL_STATUS_DOT[status]}`}
          />
          {rowHovered ? (
            <div className={`absolute right-8 top-1/2 z-[1] flex -translate-y-1/2 items-center gap-0.5 pl-1 ${selected || rowFocused ? 'bg-inbox-selected' : 'bg-app-hover'}`}>
              <RowHoverAction title="Archivieren" onClick={() => onArchive(mail)} icon={Archive} />
              <RowHoverAction title="Löschen" onClick={() => onDelete(mail)} icon={Trash2} />
              <RowHoverAction title="Als gelesen markieren" onClick={() => onMarkRead(mail)} icon={Mail} />
              <RowHoverAction title="Als erledigt markieren" onClick={() => onMarkDone(mail)} icon={Check} />
            </div>
          ) : null}
        </div>
      </li>
    );
  };

  const listScrollRef = React.useRef<HTMLDivElement>(null);
  const headerCheckRef = React.useRef<HTMLInputElement>(null);
  const [hoveredId, setHoveredId] = React.useState<string | null>(null);
  const [focusedId, setFocusedId] = React.useState<string | null>(null);
  const [draggingIds, setDraggingIds] = React.useState<string[]>([]);
  const dragPreviewRef = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => () => {
    dragPreviewRef.current?.remove();
    dragPreviewRef.current = null;
  }, []);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [searchEditable, setSearchEditable] = React.useState(false);
  const [bulkBusy, setBulkBusy] = React.useState(false);
  React.useEffect(() => {
    setSelectedIds([]);
  }, [pageIndex]);

  const animatedLeaveRef = React.useRef(new Set<string>());
  React.useEffect(() => {
    const root = listScrollRef.current;
    const leaving = new Set(leavingIds ?? []);
    for (const id of animatedLeaveRef.current) if (!leaving.has(id)) animatedLeaveRef.current.delete(id);
    if (!root) return;
    for (const id of leaving) {
      if (animatedLeaveRef.current.has(id)) continue;
      const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id.replace(/"/g, '\\"');
      const row = root.querySelector<HTMLElement>(`[data-inbox-row-item="${escaped}"]`);
      if (!row) continue;
      animatedLeaveRef.current.add(id);
      animateRowLeave(row);
    }
  }, [leavingIds]);

  const rowOrder = React.useMemo(
    () => visibleRowOrder(threads, sortMode, isMobile),
    [isMobile, sortMode, threads],
  );

  React.useEffect(() => {
    setFocusedId((current) => {
      if (current && rowOrder.some((mail) => String(mail.id) === current)) return current;
      return null;
    });
  }, [rowOrder]);

  React.useEffect(() => {
    if (!focusedId) return;
    const root = listScrollRef.current;
    if (!root) return;
    const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(focusedId) : focusedId.replace(/"/g, '\\"');
    root.querySelector<HTMLElement>(`[data-inbox-row="${escaped}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [focusedId]);

  const runBulkRef = React.useRef<((action: 'archive' | 'delete' | 'done') => Promise<void>) | null>(null);

  React.useEffect(() => {
    if (!keyboardEnabled || isMobile || initialLoading) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (isTypingTarget(event.target)) return;
      if (rowOrder.length === 0) return;

      if (event.key === 'Enter') {
        if (!focusedId) return;
        const mail = rowOrder.find((row) => String(row.id) === focusedId);
        if (!mail) return;
        event.preventDefault();
        onSelect(mail.id);
        return;
      }

      // Entf (Mac: delete = Backspace) moves the checked rows, or else the focused row, to the trash.
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedIds.length > 0) {
          event.preventDefault();
          void runBulkRef.current?.('delete');
          return;
        }
        if (!focusedId) return;
        const index = rowOrder.findIndex((row) => String(row.id) === focusedId);
        if (index < 0) return;
        event.preventDefault();
        const neighbour = rowOrder[index + 1] ?? rowOrder[index - 1] ?? null;
        setFocusedId(neighbour ? String(neighbour.id) : null);
        onDelete(rowOrder[index]);
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const ids = rowOrder.map((mail) => String(mail.id));
      const currentIndex = focusedId ? ids.indexOf(focusedId) : -1;
      let nextIndex: number;
      if (event.key === 'ArrowDown') {
        nextIndex = currentIndex < 0 ? 0 : Math.min(ids.length - 1, currentIndex + 1);
      } else {
        nextIndex = currentIndex < 0 ? ids.length - 1 : Math.max(0, currentIndex - 1);
      }
      setFocusedId(ids[nextIndex]);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [focusedId, initialLoading, isMobile, keyboardEnabled, onDelete, onSelect, rowOrder, selectedIds]);

  const visible = threads;
  const allChecked = visible.length > 0 && visible.every((mail) => selectedIds.includes(String(mail.id)));
  const selectedMails = visible.filter((mail) => selectedIds.includes(String(mail.id)));
  React.useEffect(() => {
    if (headerCheckRef.current) headerCheckRef.current.indeterminate = selectedMails.length > 0 && !allChecked;
  }, [allChecked, selectedMails.length]);

  const runBulk = async (action: 'archive' | 'delete' | 'done') => {
    if (selectedMails.length === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      await onBulkAction(selectedMails, action);
      setSelectedIds([]);
    } finally {
      setBulkBusy(false);
    }
  };
  runBulkRef.current = runBulk;
  const pageFrom = visible.length === 0 ? 0 : pageIndex * pageSize + 1;
  const pageTo = pageIndex * pageSize + visible.length;

  const skeletonRows = (
    <div className="divide-y divide-app-border">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex gap-3 px-3 py-3 animate-pulse">
          <div className="h-9 w-9 shrink-0 rounded-full bg-app-surface-muted" />
          <div className="min-w-0 flex-1 space-y-2 py-0.5">
            <div className="h-3 rounded bg-app-surface-muted w-2/3" />
            <div className="h-3 rounded bg-app-surface-muted w-full" />
            <div className="h-2.5 rounded bg-app-surface-muted w-4/5" />
          </div>
        </div>
      ))}
    </div>
  );

  let listBody: React.ReactNode;
  if (initialLoading) {
    listBody = skeletonRows;
  } else if (threads.length === 0) {
    listBody = (
      <div className="p-6 text-sm text-app-text-subtle">
        {!hasAnyEmails ? 'Keine E-Mails im Cache.' : 'Keine E-Mails in dieser Ansicht.'}
      </div>
    );
  } else if (sortMode === 'prio' && !isMobile) {
    listBody = (
      <>
        {TRIAGE_SECTIONS.map((sec) => {
          const list = visible.filter((m) => getTriageBucket(m) === sec.key);
          if (list.length === 0) return null;
          const Icon = sec.icon;
          return (
            <div key={sec.key}>
              <div className="sticky top-0 z-[1] flex items-center gap-2 px-4 py-1.5 bg-app-surface-muted border-b border-app-border">
                <Icon className={`w-3.5 h-3.5 ${sec.color}`} />
                <span className="text-xs font-medium text-app-text-muted">{sec.label}</span>
                <span className="rounded-full bg-app-surface-muted px-1.5 text-xs font-medium text-app-text-subtle">{list.length}</span>
              </div>
              <ul role="listbox" aria-label="E-Mails">{list.map(renderRow)}</ul>
            </div>
          );
        })}
      </>
    );
  } else {
    listBody = <ul role="listbox" aria-label="E-Mails">{visible.map(renderRow)}</ul>;
  }

  const shownTotal = Math.max(totalCount, pageTo);
  const pageLabel = pageFrom === 0 ? `0 von ${shownTotal}` : `${pageFrom}–${pageTo} von ${shownTotal}`;

  if (isMobile) {
    const mobileIconButton = 'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-app-text-muted active:bg-app-hover disabled:opacity-40';
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-app-surface">
        {selectedMails.length > 0 ? (
          <div className="flex h-16 shrink-0 items-center gap-1 px-2">
            <button type="button" onClick={() => setSelectedIds([])} className={mobileIconButton} aria-label="Auswahl aufheben">
              <X className="h-5 w-5" />
            </button>
            <span className="ml-1 text-base font-medium text-app-text">{selectedMails.length}</span>
            <div className="ml-auto flex items-center">
              <button type="button" disabled={bulkBusy} onClick={() => void runBulk('archive')} className={mobileIconButton} aria-label="Auswahl archivieren">
                <Archive className="h-5 w-5" strokeWidth={1.8} />
              </button>
              <button type="button" disabled={bulkBusy} onClick={() => void runBulk('delete')} className={mobileIconButton} aria-label="Auswahl löschen">
                <Trash2 className="h-5 w-5" strokeWidth={1.8} />
              </button>
              <button type="button" disabled={bulkBusy} onClick={() => void runBulk('done')} className={mobileIconButton} aria-label="Als erledigt markieren">
                <Check className="h-5 w-5 text-green-600" strokeWidth={2.2} />
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds(allChecked ? [] : visible.map((mail) => String(mail.id)))}
                className={mobileIconButton}
                aria-label={allChecked ? 'Auswahl aufheben' : 'Alle auswählen'}
              >
                <CheckCheck className={`h-5 w-5 ${allChecked ? 'text-pipedrive-blue' : ''}`} />
              </button>
            </div>
          </div>
        ) : (
          <div className="shrink-0 px-3 pb-1 pt-2">
            <div className="flex h-12 items-center gap-1 rounded-full bg-app-surface-muted px-1 shadow-sm">
              {onOpenFolders ? (
                <button type="button" onClick={onOpenFolders} className={mobileIconButton} aria-label="Ordner">
                  <Menu className="h-5 w-5" />
                </button>
              ) : (
                <Search className="ml-3 mr-1 h-5 w-5 shrink-0 text-app-text-subtle" />
              )}
              <input
                type="search"
                name="inboxQuery"
                value={searchTerm}
                onChange={(e) => onSearch(e.target.value)}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-label="Suchen"
                placeholder="In E-Mails suchen"
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
                className="h-full min-w-0 flex-1 bg-transparent text-base text-app-text outline-none placeholder:text-app-text-subtle"
              />
              <button
                type="button"
                onClick={onSync}
                disabled={emailsFetching}
                className={mobileIconButton}
                aria-label={syncButtonLabel}
                aria-busy={emailsFetching}
              >
                {syncIconSpinning ? <LoadingSpinner /> : <RefreshCw className="h-5 w-5" />}
              </button>
            </div>
          </div>
        )}
        <div ref={listScrollRef} className="min-h-0 flex-1 overflow-y-auto pb-24">
          {listBody}
          {threads.length > 0 && (pageIndex > 0 || hasMore) ? (
            <div className="flex items-center justify-center gap-2 px-4 py-4">
              <button type="button" onClick={onPrevPage} disabled={pageIndex <= 0 || loadingMore} className={mobileIconButton} aria-label="Vorherige Seite">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-xs tabular-nums text-app-text-subtle">{pageLabel}</span>
              <button type="button" onClick={onNextPage} disabled={!hasMore || loadingMore} className={mobileIconButton} aria-label="Nächste Seite">
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-app-bg p-3">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-app-border bg-app-surface">
        <div className="flex h-12 shrink-0 items-center gap-1 border-b border-app-border px-3">
          {onOpenFolders ? (
            <button
              type="button"
              onClick={onOpenFolders}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover md:hidden"
              aria-label="Ordner"
            >
              <Menu className="h-4 w-4" />
            </button>
          ) : null}
          <input
            ref={headerCheckRef}
            type="checkbox"
            checked={allChecked}
            onChange={() => {
              setSelectedIds(allChecked ? [] : visible.map((mail) => String(mail.id)));
            }}
            className="mr-1 h-4 w-4 accent-pipedrive-blue"
            aria-label="Alle auf dieser Seite auswählen"
          />
          <button
            type="button"
            onClick={onSync}
            disabled={emailsFetching}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover disabled:opacity-60"
            title={syncButtonTitle}
            aria-busy={emailsFetching}
          >
            {syncIconSpinning ? <LoadingSpinner /> : <RefreshCw className="h-4 w-4" />}
            <span className="sr-only">{syncButtonLabel}</span>
          </button>
          {selectedMails.length > 0 ? (
            <div className="ml-1 flex min-w-0 flex-1 items-center gap-0.5">
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => void runBulk('archive')}
                className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover disabled:opacity-40"
                title="Auswahl archivieren"
                aria-label="Auswahl archivieren"
              >
                <Archive className="h-4 w-4" strokeWidth={1.8} />
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => void runBulk('delete')}
                className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover disabled:opacity-40"
                title="Auswahl löschen"
                aria-label="Auswahl löschen"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.8} />
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => void runBulk('done')}
                className="ml-4 inline-flex h-7 items-center gap-1.5 rounded-md border border-app-border bg-app-surface px-2.5 text-xs font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-40"
              >
                <Check className="h-3.5 w-3.5 text-green-600" strokeWidth={2.2} />
                Als erledigt markieren
              </button>
              <span className="ml-3 shrink-0 text-xs text-app-text-subtle">
                {selectedMails.length} ausgewählt
              </span>
            </div>
          ) : (
            <div className="relative mx-2 w-64 shrink-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-text-subtle" />
              <input
                type="text"
                name="inboxQuery"
                value={searchTerm}
                onChange={(e) => onSearch(e.target.value)}
                onFocus={() => setSearchEditable(true)}
                readOnly={!searchEditable}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-label="Suchen"
                placeholder="Suchen"
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
                className="h-9 w-full rounded-md border border-app-border bg-app-surface pl-9 pr-3 text-sm text-app-text outline-none placeholder:text-app-text-subtle focus:border-app-border focus:bg-app-surface focus:ring-2 focus:ring-pipedrive-blue/20"
              />
            </div>
          )}
          <span className="ml-auto shrink-0 text-xs tabular-nums text-app-text-subtle">
            {pageLabel}
          </span>
          <button
            type="button"
            onClick={onPrevPage}
            disabled={pageIndex <= 0 || loadingMore}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover disabled:opacity-40"
            aria-label="Vorherige Seite"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onNextPage}
            disabled={!hasMore || loadingMore}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover disabled:opacity-40"
            aria-label="Nächste Seite"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div
          ref={listScrollRef}
          className="min-h-0 flex-1 overflow-y-auto app-scrollbar"
        >
          {listBody}
        </div>
      </div>
    </div>
  );
}
